import React from 'react';
import { cn } from '@/lib/utils';
import { auroraPalette } from './tokens';

export type EmptyIllustrationKind =
  | 'no-conversations'
  | 'no-messages'
  | 'no-workgroups'
  | 'no-posts'
  | 'no-bookmarks'
  | 'no-search'
  | 'no-reports';

export interface EmptyStateProps {
  illustration: EmptyIllustrationKind;
  title: string;
  subtitle?: string;
  cta?: React.ReactNode;
  className?: string;
  compact?: boolean;
}

// Task #1665 — 7 illustrazioni SVG aurora inline. Stroke/fill via
// `currentColor` per parity dark/light automatica; gradiente aurora indigo
// → violet/fuchsia per accentuare l'identità.
function Illustration({ kind }: { kind: EmptyIllustrationKind }) {
  const gradId = `aur-${kind}`;
  const C = auroraPalette;
  const common = (
    <defs>
      <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={C.indigo} stopOpacity="0.85" />
        <stop offset="60%" stopColor={C.violet} stopOpacity="0.55" />
        <stop offset="100%" stopColor={C.fuchsia} stopOpacity="0.4" />
      </linearGradient>
    </defs>
  );
  const stroke = C.slateSoft;

  switch (kind) {
    case 'no-conversations':
      return (
        <svg viewBox="0 0 160 120" className="w-full h-full" aria-hidden="true">
          {common}
          <rect x="14" y="20" width="92" height="60" rx="14" fill={`url(#${gradId})`} opacity="0.18" />
          <rect x="14" y="20" width="92" height="60" rx="14" stroke={stroke} strokeWidth="1.5" fill="none" />
          <path d="M30 80 L40 96 L52 80" stroke={stroke} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          <rect x="56" y="44" width="68" height="56" rx="14" fill={`url(#${gradId})`} opacity="0.32" />
          <rect x="56" y="44" width="68" height="56" rx="14" stroke={C.violet} strokeWidth="1.5" fill="none" />
          <circle cx="74" cy="68" r="3" fill={C.violet} />
          <circle cx="90" cy="68" r="3" fill={C.violet} />
          <circle cx="106" cy="68" r="3" fill={C.violet} />
        </svg>
      );
    case 'no-messages':
      return (
        <svg viewBox="0 0 160 120" className="w-full h-full" aria-hidden="true">
          {common}
          <path d="M30 36 H110 a12 12 0 0 1 12 12 v28 a12 12 0 0 1 -12 12 H62 L46 100 V88 H30 a12 12 0 0 1 -12 -12 V48 A12 12 0 0 1 30 36 Z"
            fill={`url(#${gradId})`} opacity="0.25" stroke={C.indigo} strokeWidth="1.5" />
          <circle cx="56" cy="62" r="2.5" fill={C.indigo} />
          <circle cx="70" cy="62" r="2.5" fill={C.indigo} />
          <circle cx="84" cy="62" r="2.5" fill={C.indigo} />
        </svg>
      );
    case 'no-workgroups':
      return (
        <svg viewBox="0 0 160 120" className="w-full h-full" aria-hidden="true">
          {common}
          <circle cx="60" cy="60" r="22" fill={`url(#${gradId})`} opacity="0.30" stroke={C.violet} strokeWidth="1.5" />
          <circle cx="100" cy="60" r="22" fill={`url(#${gradId})`} opacity="0.30" stroke={C.fuchsia} strokeWidth="1.5" />
          <circle cx="80" cy="92" r="22" fill={`url(#${gradId})`} opacity="0.30" stroke={C.indigo} strokeWidth="1.5" />
          <circle cx="60" cy="60" r="6" fill={C.violet} />
          <circle cx="100" cy="60" r="6" fill={C.fuchsia} />
          <circle cx="80" cy="92" r="6" fill={C.indigo} />
        </svg>
      );
    case 'no-posts':
      return (
        <svg viewBox="0 0 160 120" className="w-full h-full" aria-hidden="true">
          {common}
          <rect x="24" y="22" width="112" height="22" rx="6" fill={`url(#${gradId})`} opacity="0.22" />
          <rect x="24" y="50" width="112" height="22" rx="6" fill={`url(#${gradId})`} opacity="0.32" />
          <rect x="24" y="78" width="112" height="22" rx="6" fill={`url(#${gradId})`} opacity="0.22" />
          <circle cx="38" cy="33" r="5" fill={C.violet} />
          <circle cx="38" cy="61" r="5" fill={C.fuchsia} />
          <circle cx="38" cy="89" r="5" fill={C.indigo} />
          <line x1="52" y1="33" x2="120" y2="33" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" />
          <line x1="52" y1="61" x2="108" y2="61" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" />
          <line x1="52" y1="89" x2="116" y2="89" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    case 'no-bookmarks':
      return (
        <svg viewBox="0 0 160 120" className="w-full h-full" aria-hidden="true">
          {common}
          <path d="M60 22 H100 a8 8 0 0 1 8 8 V100 L80 84 L52 100 V30 A8 8 0 0 1 60 22 Z"
            fill={`url(#${gradId})`} opacity="0.32" stroke={C.violet} strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M70 46 H90 M70 58 H86" stroke={C.violet} strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    case 'no-search':
      return (
        <svg viewBox="0 0 160 120" className="w-full h-full" aria-hidden="true">
          {common}
          <circle cx="70" cy="58" r="28" fill={`url(#${gradId})`} opacity="0.22" stroke={C.indigo} strokeWidth="2" />
          <line x1="90" y1="78" x2="116" y2="104" stroke={C.violet} strokeWidth="4" strokeLinecap="round" />
          <line x1="58" y1="48" x2="82" y2="72" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" />
          <line x1="82" y1="48" x2="58" y2="72" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    case 'no-reports':
      return (
        <svg viewBox="0 0 160 120" className="w-full h-full" aria-hidden="true">
          {common}
          <path d="M50 26 H110 a10 10 0 0 1 10 10 V90 a10 10 0 0 1 -10 10 H50 a10 10 0 0 1 -10 -10 V36 A10 10 0 0 1 50 26 Z"
            fill={`url(#${gradId})`} opacity="0.22" stroke={C.indigo} strokeWidth="1.5" />
          <path d="M62 60 L74 72 L98 48" stroke={C.emerald} strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
  }
}

export function EmptyState({ illustration, title, subtitle, cta, className, compact = false }: EmptyStateProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex flex-col items-center justify-center text-center',
        compact ? 'gap-2 py-6 px-4' : 'gap-3 py-12 px-6',
        className,
      )}
    >
      <div className={cn('text-gray-400 dark:text-gray-500', compact ? 'w-28 h-20' : 'w-40 h-28')}>
        <Illustration kind={illustration} />
      </div>
      <h3 className={cn('font-semibold text-gray-700 dark:text-gray-200', compact ? 'text-sm' : 'text-base')}>
        {title}
      </h3>
      {subtitle && (
        <p className={cn('text-gray-500 dark:text-gray-400 max-w-sm', compact ? 'text-xs' : 'text-sm')}>
          {subtitle}
        </p>
      )}
      {cta && <div className="mt-2">{cta}</div>}
    </div>
  );
}
